import { OwnerCallToActionCardData, OwnerCallToActionImageProps } from "data/types";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaHome, FaStar, FaEye, FaHeart, FaMapMarkerAlt } from "react-icons/fa";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

import { OWNER_CALL_TO_ACTION_IMAGES } from "data/constants";
import { EASING } from "../../utils/easingUtils";

// Animation variants for cards
const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: EASING.easeOut },
  },
  hover: {
    scale: 1.05,
    y: -8,
    transition: { duration: 0.3, ease: EASING.easeOut },
  },
};

// Container animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

// Title animation variants
const titleVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASING.easeOut },
  },
};

// Reusable CallToActionImage Component
function OwnerCallToActionImage({
  title = "Votre espace mérite d'être vu",
  description = "Découvrez notre sélection d'espaces événementiels disponibles à Paris et en Île-de-France. Que vous cherchiez une salle de réception, un studio, un jardin ou même une péniche, vous trouverez ici le lieu parfait pour tous vos événements, privés ou professionnels",
  cardData = OWNER_CALL_TO_ACTION_IMAGES,
  className = "relative overflow-hidden",
  controls,
}: OwnerCallToActionImageProps) {
  return (
    <div
      className={`${className} bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900`}
    >
      {/* Background Effects */}
      <BgGlassmorphism />
      <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

      <div className="container relative px-4 py-16 mx-auto">
        {/* Hero Section */}
        <motion.div className="text-center mb-16" variants={titleVariants} initial="hidden" animate="visible">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
            <FaHome className="w-4 h-4 mr-2 text-pink-500" />
            Espaces disponibles
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
            {title}
          </h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>

          {/* Statistics */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaEye className="w-5 h-5 text-pink-500 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">1000+ visiteurs/mois</span>
            </div>
            <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaHeart className="w-5 h-5 text-rose-500 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">Espaces favoris</span>
            </div>
            <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaMapMarkerAlt className="w-5 h-5 text-pink-600 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">Paris & Île-de-France</span>
            </div>
          </div>
        </motion.div>
        {/* Spaces Grid */}
        <motion.div className="max-w-6xl mx-auto" variants={containerVariants} initial="hidden" animate="visible">
          <div className="space-y-8">
            {/* First Row: 3 Images */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-12">
              {cardData.slice(0, 3).map((card, index) => (
                <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                  <Card card={card} index={index} />
                </div>
              ))}
            </div>

            {/* Second Row: 2 Images */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-9">
              {cardData.slice(3, 5).map((card, index) => (
                <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                  <Card card={card} index={index + 3} />
                </div>
              ))}
            </div>

            {/* Third Row: 3 Images */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-12">
              {cardData.slice(5, 8).map((card, index) => (
                <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                  <Card card={card} index={index + 5} />
                </div>
              ))}
            </div>

            {/* Fourth Row: 2 Images */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-9">
              {cardData.slice(8, 10).map((card, index) => (
                <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                  <Card card={card} index={index + 8} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <div className="relative overflow-hidden bg-white dark:bg-neutral-800 rounded-3xl shadow-xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-40 h-40 bg-pink-500 rounded-full -translate-x-20 -translate-y-20"></div>
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-rose-500 rounded-full translate-x-16 translate-y-16"></div>
            </div>

            <div className="relative grid md:grid-cols-2 gap-8 p-8">
              {/* Left Side - Content */}
              <div className="flex flex-col justify-center">
                <div className="inline-flex items-center px-3 py-1 bg-pink-50 text-pink-600 rounded-full text-sm font-medium mb-4 w-fit">
                  <FaStar className="w-4 h-4 mr-2" />
                  Propriétaire d'espace
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                  Votre espace mérite une meilleure visibilité
                </h3>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-6 leading-relaxed">
                  Rejoignez EventToBee et donnez de la visibilité à votre espace auprès de milliers de clients à la
                  recherche du lieu parfait.
                </p>

                <Link to="/add-location">
                  <button className="group bg-gradient-to-r from-pink-500 to-rose-500 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg hover:shadow-xl w-fit">
                    <span className="flex items-center">
                      Ajouter mon espace
                      <svg
                        className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        ></path>
                      </svg>
                    </span>
                  </button>
                </Link>
              </div>

              {/* Right Side - Visual/Stats */}
              <div className="flex flex-col justify-center items-center">
                <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-pink-600 mb-1">500+</div>
                    <div className="text-sm text-neutral-600">Espaces</div>
                  </div>
                  <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-rose-600 mb-1">2000+</div>
                    <div className="text-sm text-neutral-600">Événements</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-1">95%</div>
                    <div className="text-sm text-neutral-600">Satisfaction</div>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-2xl text-center">
                    <div className="text-2xl font-bold text-pink-600 mb-1">0€</div>
                    <div className="text-sm text-neutral-600">Frais d'inscription</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      {/* RTL Support */}
      <style>{`
          [dir='rtl'] .discount-single-item .discount {
            left: auto;
            right: 1.25rem;
          }
        `}</style>
    </div>
  );
}

// Reusable Card Component
function Card({
  card,
  index,
  className = "",
}: {
  card: OwnerCallToActionCardData;
  index: number;
  className?: string;
}) {
  return (
    <AnimatePresence>
      <motion.div
        className={`group relative block rounded-3xl overflow-hidden ${className} shadow-lg hover:shadow-2xl transition-shadow duration-500`}
        style={{ height: card.height }}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        transition={{ delay: index * 0.1 }}
      >
        <Link to={card.to} className="block w-full h-full">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-center bg-cover transform group-hover:scale-110 transition-transform duration-700"
            style={{ backgroundImage: `url(${card.image})` }}
          />

          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Decorative Elements */}
          <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
            <FaStar className="w-5 h-5 text-white" />
          </div>

          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
            >
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-pink-500/90 backdrop-blur-sm rounded-full text-xs font-medium text-white shadow-lg">
                  Disponible
                </span>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 md:text-xl group-hover:text-pink-600 transition-colors duration-300 drop-shadow-lg">
                {card.title}
              </h3>
              <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm text-neutral-800 font-medium drop-shadow-md">Découvrir cet espace →</span>
              </div>
            </motion.div>
          </div>

          {/* Border Effect */}
          <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-pink-500/50 transition-colors duration-500" />
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}

export default OwnerCallToActionImage;
